



































































































































































































































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import CommonVue from '../models/commonVue';
import Topic from '../api/topic';
import School from '../api/school';
import ApiList from '../api/list';
import Media from '../api/media';
import LockManager from '../models/lock';

import { SimpleFileField, MediaFileField } from '../models/fields';
import { BusEvent } from '../models/bus';
import FieldImage from '../components/FieldImage.vue';
import FieldInternational from '../components/FieldInternational.vue';
import DropDownEntity from '../components/DropDownEntity.vue';
import DropDownText from '../components/DropDownText.vue';
import EditIcon from '../components/icons/EditIcon.vue';
import MultiDropdownEntity from '@/components/MultiDropdownEntity.vue';
import Category from '@/api/category';
import FieldVideos from '@/components/FieldVideos.vue';
import MultiDropdownList from '@/components/MultiDropdownList.vue';
import { Tags } from '@/api/common';
import FieldInterfaceElements from '@/components/FieldInterfaceElements.vue';

@Component({
  components: {
    MultiDropdownList,
    'field-videos': FieldVideos,
    'drop-down-entity': DropDownEntity,
    'drop-down-text': DropDownText,
    'field-international': FieldInternational,
    'field-image': FieldImage,
    'edit-icon': EditIcon,
    'multi-dropdown-entity': MultiDropdownEntity,
    'field-interface-elements': FieldInterfaceElements
  }
})
export default class EditTopic extends CommonVue {
  @Prop({ type: Number }) public id?: number;

  public categoryList: ApiList<Category> = null;
  public schoolList: ApiList<School> = null;
  public topic: Topic = null;
  public topicName: string = '';

  public tagOptions: string[] = Tags.OPTIONS;
  public tagPresentation: string[] = Tags.PRESENTATION;

  // do not show schools if there is only 1 school
  public showSchools: boolean = false;

  public async beforeMount() {
    await this.load();
  }

  @Watch('id')
  public async load() {
    this.busy = true;
    this.error = false;

    this.ignoreFirstEntityChange = true;
    await this.loadTopic();
    this.ignoreFirstEntityChange = false;
    this.updateName();

    if (this.topic && this.topic.id) {
      this.lockManager = new LockManager(this.TOPIC_SLUG, this.topic.id);
      await this.lockManager.checkStatus();
      if (this.lockManager.isBusy) {
        this.$bus.$emit(
          BusEvent.ERROR,
          'Someone else is making changes.',
          new Error(
            'Click refresh to check if they have finished their changes'
          )
        );
      }
    } else {
      this.lockManager = null;
    }

    await this.loadSchoolList();
    await this.loadCategoryList();

    this.busy = false;
  }

  public async loadTopic(): Promise<void> {
    if (!this.id) {
      this.topic = new Topic();
    } else {
      try {
        this.topic = await Topic.get(this.id);
      } catch (e) {
        this.error = true;
        this.$bus.$emit(BusEvent.ERROR, 'Could not get topic information', e);
      }
    }
  }

  public updateName() {
    let name = this.topic.name.getDefaultValue();
    if (!name) {
      name = '';
    }
    this.topicName = name;
  }

  public async loadSchoolList() {
    try {
      this.schoolList = await School.listAll();

      // hide school field if there is only 1 school and assign it as default
      if (this.topic && this.schoolList && this.schoolList.length === 1) {
        this.topic.school = this.schoolList.getIndex(0).id;
      } else {
        this.showSchools = true;
      }
    } catch (e) {
      this.$bus.$emit(BusEvent.ERROR, 'Could not load schools list', e);
      this.error = true;
    }
  }

  public async loadCategoryList() {
    try {
      this.categoryList = await Category.listAll();
    } catch (e) {
      this.$bus.$emit(BusEvent.ERROR, 'Could not load category list', e);
      this.error = true;
    }
  }

  // enter edit mode if it can get the lock
  @Watch('topic', { deep: true })
  public async enterEditMode() {
    await this.tryToLock();
  }

  public async deleteClicked(): Promise<void> {
    if (!this.topic || !this.topic.id) {
      return;
    }

    this.busy = true;

    try {
      if (!this.lockManager) {
        this.lockManager = new LockManager(this.TOPIC_SLUG, this.topic.id);
      }

      await this.tryToLock();

      await this.topic.delete();

      await this.lockManager.releaseLock();

      this.busy = false;
      this.$router.push(`/${this.TOPIC_SLUG}/`);
    } catch (e) {
      this.busy = false;
      this.$bus.$emit(BusEvent.ERROR, 'Could not delete topic', e);
    }
  }

  public async saveClicked(): Promise<void> {
    if (!this.topic) {
      return;
    }
    this.busy = true;
    try {
      this.checkMandatoryFields();
      await this.saveTopic();

      this.busy = false;
      this.$router.push(`/${this.TOPIC_SLUG}/${this.topic.id}`);
    } catch (e) {
      this.busy = false;
      this.$bus.$emit(BusEvent.ERROR, 'Could not save topic', e);
    }
  }

  public checkMandatoryFields() {
    if (!this.topic) {
      throw Error('Unable to create Topic.');
    }

    // Check if name is filled
    const defaultName = this.topic.name.getDefaultValue();
    if (defaultName === undefined || defaultName === '') {
      throw Error('Please insert an English name for your topic.');
    }

    // Check if school field is filled
    if (this.topic.school === undefined || this.topic.school === null) {
      throw Error('Please select a school.');
    }

    // Check if background image file was chosen
    // NOTE: following is an ugly hack to avoid an error from the compiler
    const key = 'bg_image';
    const test = (this.$refs[key] as any) as MediaFileField;
    if (!test.hasMedia()) {
      throw Error('Please select a background image.');
    }
  }

  public async saveTopic(): Promise<void> {
    if (!this.topic) {
      throw Error('Unable to save.');
    }

    const isNew = !this.topic.id;
    if (!isNew) {
      if (!this.lockManager && this.topic && this.topic.id) {
        this.lockManager = new LockManager(this.TOPIC_SLUG, this.topic.id);
      }
      await this.tryToLock();
      // Upload media files (if exercise already created)
      await this.saveMediaFiles();
    }
    // Upload topic
    await this.topic.save();

    if (isNew) {
      await this.saveTopic();
    }

    if (this.lockManager && this.lockManager.isLocked) {
      await this.lockManager.releaseLock();
    }
  }

  public async saveMediaFiles(): Promise<void> {

    if (!this.topic || !this.topic.id) {
      throw Error('Topic has not been created yet.');
    }

    this.imagesToDelete.forEach(async (id) => await Media.deleteImage(id));
    this.imagesToDelete = [];

    this.audioToDelete.forEach(async (id) => await Media.deleteAudio(id));
    this.audioToDelete = [];

    const bg_image = await this.saveMediaFile(this.$refs.bg_image, 'bg_image');
    if (bg_image !== null) {
      this.topic.bg_image = bg_image;
    }
    
    const banner_image = await this.saveMediaFile(this.$refs.banner_image,'banner_image');
    if (banner_image !== null) {
      this.topic.banner_image = banner_image;
    }

    const cert_bg = await this.saveMediaFile(this.$refs.cert_bg, 'cert_bg');
      if (cert_bg !== null) {
        this.topic.cert_bg = cert_bg;
      }

    const cert_detail_bg = await this.saveMediaFile(this.$refs.cert_detail_bg, 'cert_detail_bg');
    if (cert_detail_bg !== null) {
      this.topic.cert_detail_bg = cert_detail_bg;
    }

    const cert_sharing_bg = await this.saveMediaFile(this.$refs.cert_sharing_bg, 'cert_sharing_bg');
    if (cert_sharing_bg !== null) {
      this.topic.cert_sharing_bg = cert_sharing_bg;
    }

    if (!this.isNew) {
      const icon = await this.saveMediaFile(this.$refs.icon, 'icon');
      if (icon !== null) {
        this.topic.icon = icon;
      }
    }
  }

  public async saveMediaFile(
    field: any,
    fieldName: string
  ): Promise<number | null | undefined> {
    if (!this.topic || !this.topic.id) {
      return null;
    }
    const mediaFileField = (field as any) as MediaFileField;
    console.log('mediaFileField >> ', mediaFileField);
    
    if (mediaFileField.hasNewMedia()) {
      return await mediaFileField.upload(Topic.SLUG, this.topic.id, fieldName);
    } else if (!mediaFileField.hasMedia()) {
      return undefined;
    } else {
      return null;
    }
  }
}
